/**
 * UploadPicker — iter-435f Phase B.
 *
 * Small dialog that lets an admin pick from the existing bookshop uploads
 * library and returns the URL. Wired next to product-form URL inputs so
 * users don't have to switch to the Uploads tab, copy a URL, and paste it back.
 *
 * Usage:
 *   <UploadPicker
 *     kind="images"                        // filters the list
 *     value={form.cover_image_url}
 *     onSelect={(url) => set('cover_image_url', url)}
 *   />
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Library, Check, RefreshCw, FileText } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const KIND_LABELS = {
  images: 'Images', books: 'Books', audio: 'Audio', video: 'Video',
  private: 'Private',
};

export function UploadPicker({ kind = 'images', value, onSelect,
                                  label = 'Pick from library',
                                  triggerTestId = 'uploads-picker-trigger',
                                  filterKinds = null }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeKind, setActiveKind] = useState(kind);
  const kinds = filterKinds || [kind];

  const load = async () => {
    setBusy(true);
    try {
      const url = activeKind
        ? `${API}/api/bookshop/admin/uploads?kind=${activeKind}&limit=200`
        : `${API}/api/bookshop/admin/uploads?limit=200`;
      const r = await axios.get(url, { headers: authH() });
      setRows(r.data.uploads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load uploads');
    } finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, activeKind]);

  const pick = (u) => {
    const fullUrl = u.url?.startsWith('http') ? u.url : `${API}${u.url}`;
    onSelect(fullUrl);
    toast.success('URL inserted');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline"
                  className="h-8 shrink-0"
                  data-testid={triggerTestId}>
          <Library className="h-3 w-3 mr-1" />{label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Pick from uploads library</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          {kinds.length > 1 && kinds.map((k) => (
            <Badge key={k} variant="outline"
                     onClick={() => setActiveKind(k)}
                     className={`cursor-pointer ${activeKind === k
                       ? 'border-indigo-400 text-indigo-600 bg-indigo-50'
                       : 'border-slate-300 text-slate-500'}`}
                     data-testid={`uploads-picker-kind-${k}`}>
              {KIND_LABELS[k] || k}
            </Badge>
          ))}
          <Button type="button" size="sm" variant="outline"
                    className="ml-auto h-7" onClick={load}
                    data-testid="uploads-picker-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <div className="max-h-96 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
          {!busy && !rows.length && (
            <div className="col-span-full text-center py-8 text-sm text-slate-500">
              No uploads yet — upload some from the Uploads tab.
            </div>
          )}
          {rows.map((u) => {
            const fullUrl = u.url?.startsWith('http') ? u.url : `${API}${u.url}`;
            const isImage = u.kind === 'images';
            const active = fullUrl === value || u.url === value;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => pick(u)}
                className={`border rounded-md p-2 text-left hover:bg-slate-50 transition ${
                  active ? 'border-indigo-500 ring-2 ring-indigo-200'
                          : 'border-slate-200'
                }`}
                data-testid={`uploads-picker-tile-${u.id}`}
              >
                <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden rounded">
                  {isImage
                    ? <img src={fullUrl} alt={u.original_filename}
                             className="max-h-full max-w-full object-contain" />
                    : <FileText className="h-8 w-8 text-slate-400" />}
                </div>
                <div className="text-xs text-slate-700 mt-1 truncate">
                  {u.original_filename || u.filename}
                </div>
                <div className="text-[10px] text-slate-500">
                  {u.kind} · {Math.round((u.size_bytes || 0) / 1024)} KB
                </div>
                {active && (
                  <Badge variant="outline" className="mt-1 text-[9px] border-indigo-500 text-indigo-600">
                    <Check className="h-2.5 w-2.5 mr-1" />current
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UploadPicker;
